/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Dropdown } from 'tea-component';
import { MainContentCmp } from '../../ChatPage/components/SubComp';
import { HTMLToMarkdownConverter, isDeleteAffectingElement } from '../utils';

interface CustomDropdownProps {
  menu: (close: () => void) => React.ReactNode;
  onClose?: () => void;
  button?: React.ReactNode;
  style?: React.CSSProperties;
  overlayClassName?: string;
  /** 是否禁用下拉（tea Dropdown 禁用态，禁用后不可展开） */
  disabled?: boolean;
}

interface MarkdownEditorProps {
  value: string;
  answer?: any;
}

interface MarkdownEditorHandle {
  getMarkdown: () => Promise<string>;
  ref: React.RefObject<HTMLDivElement>;
  handleSetHtml: () => void;
}

export const CustomDropdown: React.FC<CustomDropdownProps> = ({
  menu,
  onClose,
  button,
  overlayClassName,
  ...restProps
}) => (
  <Dropdown
    trigger="hover"
    clickClose={false}
    style={{ marginRight: 10 }}
    button={button}
    overlayClassName={`dba-custom-dropdown-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`}
    onClose={() => {
      onClose?.();
    }}
    {...restProps}
  >
    {menu}
  </Dropdown>
);

export function MarkdownEditorInner({ value, answer }, ref) {
  const editableRef = useRef(null);
  const [contentEditableFlag, setContentEditableFlag] = useState(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const converter = new HTMLToMarkdownConverter();
  // DnD refs
  const dragNodeRef = useRef<HTMLElement | null>(null);
  const lastCaretRangeRef = useRef<Range | null>(null);
  // 脏标记：用户是否在 contentEditable 中进行了编辑操作
  const isDirtyRef = useRef(false);
  
  useEffect(() => {
    const editableDiv = editableRef.current;
    if (!editableDiv) return;
  
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
  
      const selection = window.getSelection();
      if (!selection?.rangeCount) return;
  
      const range = selection.getRangeAt(0);
      const protectedElement = editableDiv.querySelector('.dba-agent-bid');
      if (isDeleteAffectingElement(range, protectedElement, e.key)) {
        e.preventDefault();
        console.log('禁止删除图表容器！');
      }
    };
  
    editableDiv.addEventListener('keydown', handleKeyDown);
    return () => editableDiv.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Setup drag and drop for images and bid blocks within contentEditable
  useEffect(() => {
    // 获取可编辑区域的根元素
    const root = editableRef.current as HTMLElement | null;
    if (!root) return;

    // 判断目标元素是否可拖拽（图表块或图片）
    const isDraggableTarget = (el: HTMLElement | null) => {
      if (!el?.closest) return false; // 确保 el 是有效的 DOM 元素
      if (el.closest('.dba-agent-bid')) return true; // 图表块整体拖拽
      if (el.tagName && el.tagName.toLowerCase() === 'img') return true; // 图片拖拽
      return false;
    };

    // 获取拖拽元素（图表块或图片）
    const getDragElement = (el: HTMLElement): HTMLElement => {
      const bid = el.closest('.dba-agent-bid') as HTMLElement | null;
      return bid || el;
    };

    // 拖拽开始事件处理
    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (!isDraggableTarget(target)) return;
      const dragEl = getDragElement(target);
      dragNodeRef.current = dragEl;
      
      // 添加取消图片选中态的类名
      const root = editableRef.current;
      if (root) {
        root.classList.add('dba-no-image-selection');
      }
      
      // 使用自定义拖拽预览图避免大尺寸预览
      //       if (dragEl) {
      // // 获取这个元素的内容,生成一个缩略图,下面代码进行改造
      //           // className="dba-agent-bid-drag-image"
      //         // const rect = dragEl.getBoundingClientRect();
      //         // const canvas = document.createElement('canvas');
      //         // canvas.width = Math.min(200, rect.width);
      //         // canvas.height = Math.min(120, rect.height);
      //         // const ctx = canvas.getContext('2d');
      //         // if (ctx) {
      //         //   ctx.fillStyle = 'rgba(0,0,0,0.05)';
      //         //   ctx.fillRect(0, 0, canvas.width, canvas.height);
      //         // }
      //         // e.dataTransfer?.setDragImage(canvas, 0, 0);
      //         // e.dataTransfer?.setData('text/plain', '__internal_dnd__');
      //       }
    };
    
    // 检查 range 的各个节点是否在表格内
    const checkInTable = (node: Node | null): boolean => {
      if (!node) return false;
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.closest('table')) return true;
      }
      if (node.parentElement?.closest('table')) return true;
      return false;
    };

    // 拖拽悬停事件处理
    const handleDragOver = (e: DragEvent) => {
      if (!dragNodeRef.current) return;
      e.preventDefault();
      
      // 检查鼠标位置是否在源图表上
      const sourceEl = dragNodeRef.current;
      const sourceRect = sourceEl.getBoundingClientRect();
      const isOverSource = 
        e.clientX >= sourceRect.left && 
        e.clientX <= sourceRect.right && 
        e.clientY >= sourceRect.top && 
        e.clientY <= sourceRect.bottom;
      // 检查目标位置是否在图表块内
      const targetEl = document.elementFromPoint(e.clientX, e.clientY);
      // 如果在源图表上，直接返回
      if (isOverSource || targetEl?.closest('.dba-agent-bid')) return;
      
      
      // 移除旧的示意线
      const oldLine = document.querySelector('.dba-drag-drop-line');
      if (oldLine) {
        oldLine.remove();
      }
      
      // 根据鼠标位置获取光标范围
      const range = document.caretRangeFromPoint
        ? document.caretRangeFromPoint(e.clientX, e.clientY)
        : null;
      if (range) {
        // 检查 range 的起始和结束容器是否在表格内
        if (checkInTable(range.startContainer) || 
            checkInTable(range.endContainer) || 
            checkInTable(range.commonAncestorContainer)) {
          return; // 如果在表格内，不允许拖拽
        }
        
        // 检查范围是否在源图表内
        const rangeEl = range.commonAncestorContainer.parentElement;
        if (rangeEl && sourceEl.contains(rangeEl)) return;
        
        lastCaretRangeRef.current = range;
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        
        // 创建底部示意线
        const dropLine = document.createElement('div');
        dropLine.className = 'dba-drag-drop-line';
        dropLine.style.height = '2px';
        dropLine.style.background = '#3C7BFF';
        dropLine.style.borderRadius = '2px';
        dropLine.style.margin = '6px 0';
        dropLine.style.position = 'relative';
        dropLine.style.zIndex = '1000';
        dropLine.style.animation = 'dropLinePulse 1.5s ease-in-out infinite';
        
        // 将示意线插入到光标位置
        try {
          range.insertNode(dropLine);
        } catch (error) {
          // 如果插入失败，尝试插入到父元素
          const parent = range.commonAncestorContainer.parentElement;
          if (parent) {
            parent.appendChild(dropLine);
          }
        }
      }
    };

    // 在指定范围插入节点
    const insertNodeAtRange = (node: Node, range: Range) => {
      // 避免在节点内部插入
      if (node.contains(range.commonAncestorContainer)) {
        return;
      }
      range.insertNode(node);
      // 将光标移动到插入节点之后
      const sel = window.getSelection();
      sel?.removeAllRanges();
      const after = document.createTextNode('\u00A0');
      if (node.parentNode) node.parentNode.insertBefore(after, node.nextSibling);
      const newRange = document.createRange();
      newRange.setStartAfter(after);
      newRange.collapse(true);
      sel?.addRange(newRange);
    };

    // 拖拽放置事件处理
    const handleDrop = (e: DragEvent) => {
      if (!dragNodeRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      
      // 检查鼠标位置是否在表格内或图表块内
      const targetEl = document.elementFromPoint(e.clientX, e.clientY);
      if (targetEl?.closest('table') || targetEl?.closest('.dba-agent-bid')) {
        // 如果在表格内或图表块内，清理状态并返回
        const dropLine = document.querySelector('.dba-drag-drop-line');
        if (dropLine) {
          dropLine.remove();
        }
        dragNodeRef.current = null;
        lastCaretRangeRef.current = null;
        return;
      }
      
      // 移除示意线
      const dropLine = document.querySelector('.dba-drag-drop-line');
      if (dropLine) {
        dropLine.remove();
      }
      
      // 获取拖拽放置位置的光标范围
      const dropRange = ((): Range | null => {
        if (document.caretRangeFromPoint) {
          return document.caretRangeFromPoint(e.clientX, e.clientY);
        }
        return lastCaretRangeRef.current;
      })();
      
      // 检查 dropRange 是否会导致插入到表格内
      if (dropRange) {
      
        
        // 如果 dropRange 的任何容器在表格内，则取消放置
        if (checkInTable(dropRange.startContainer) || 
            checkInTable(dropRange.endContainer) || 
            checkInTable(dropRange.commonAncestorContainer)) {
          dragNodeRef.current = null;
          lastCaretRangeRef.current = null;
          return;
        }
      }
      
      const dragEl = dragNodeRef.current;
      // 确保元素在根元素内
      if (dropRange && root && root.contains(dragEl)) {
        // 临时移除并插入到光标位置
        const placeholder = document.createComment('drag-placeholder');
        dragEl.parentNode?.insertBefore(placeholder, dragEl);
        dragEl.remove();
        insertNodeAtRange(dragEl, dropRange);
        placeholder.remove();
      }
      dragNodeRef.current = null;
      lastCaretRangeRef.current = null;
    };

    // 鼠标按下事件处理
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (isDraggableTarget(target)) {
        const dragEl = getDragElement(target);
        // 设置可拖拽属性
        dragEl.setAttribute('draggable', 'true');
      }
    };

    // 拖拽结束事件处理
    const handleDragEnd = () => {
      // 清理示意线
      const dropLine = document.querySelector('.dba-drag-drop-line');
      if (dropLine) {
        dropLine.remove();
      }
      // 移除取消图片选中态的类名
      const root = editableRef.current;
      if (root) {
        root.classList.remove('dba-no-image-selection');
      }
      dragNodeRef.current = null;
      lastCaretRangeRef.current = null;
    };

    // 添加事件监听器
    root.addEventListener('dragstart', handleDragStart);
    root.addEventListener('dragover', handleDragOver);
    root.addEventListener('drop', handleDrop);
    root.addEventListener('dragend', handleDragEnd);
    root.addEventListener('mousedown', handleMouseDown, true);

    // 初始化可拖拽元素
    const initDraggables = () => {
      root.querySelectorAll('img, .dba-agent-bid').forEach((el) => {
        (el as HTMLElement).setAttribute('draggable', 'true');
      });
    };
    initDraggables();

    // 清理事件监听器
    return () => {
      root.removeEventListener('dragstart', handleDragStart);
      root.removeEventListener('dragover', handleDragOver);
      root.removeEventListener('drop', handleDrop);
      root.removeEventListener('dragend', handleDragEnd);
      root.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [contentEditableFlag]);

  // 获取Markdown内容
  const handleGetHtml = useCallback(async () => {
    const htmlContent = document.querySelector(".dba-chat-ds-report-page-right-markdown-report")?.innerHTML || '';
    const markdown = await converter.convertAsync(htmlContent);
    return markdown;
  }, [converter]);

  // 监听 contentEditable 的 input 事件，标记用户已编辑
  // input 事件仅在用户操作（打字、删除、粘贴、拖拽等）时触发，
  // 不会在程序设置 innerHTML / key 强刷时触发，因此可以精确判断用户是否修改了内容
  useEffect(() => {
    const editableDiv = editableRef.current;
    if (!editableDiv) return;
    const handleInput = () => {
      isDirtyRef.current = true;
    };
    editableDiv.addEventListener('input', handleInput);
    return () => editableDiv.removeEventListener('input', handleInput);
  }, [contentEditableFlag]);

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    getMarkdown: handleGetHtml,
    ref,
    handleSetHtml: () => setContentEditableFlag(false),
    // 查询用户是否编辑过内容（自上次 resetDirty 以来）
    isDirty: () => isDirtyRef.current,
    // 重置脏标记（保存成功或文档重新加载后调用）
    resetDirty: () => { isDirtyRef.current = false },
  }));

  useEffect(() => {
    setContentEditableFlag(true);
  }, [contentEditableFlag]);
  
  return (
    <>
      <div
        contentEditable
        ref={editableRef}
        // 这个key用来强刷组件，不然，外部的value同步不了最新值，罪魁祸首是contentEditable这个属性，
        key={contentEditableFlag}
      >
        <MainContentCmp
          {...{
            value,
            answer,
            from: 'ds-agent'
          }}
        />
      </div>
    </>
  );
}

export const MarkdownEditor = React.forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(
  MarkdownEditorInner
);
